import _ from 'lodash';
import { defineMessages } from 'react-intl';

import DataGroupingOption from '../../models/DataGroupingOption';
import DataOption from '../../models/DataOption';
import StatMessages from '../../StatMessages';
import getHoursOffset from '../../../utils/getHoursOffset';

export default class CallQueueReport {
  constructor(intl) {
    if (!intl) {
      throw Error('Missing intl param');
    }

    const messages = defineMessages({
      AGGREGATED: {
        id: 'AGGREGATED',
        defaultMessage: 'Aggregate',
      },
      PER_AGENT: {
        id: 'PER_AGENT',
        defaultMessage: 'Per Agent',
      },
      PER_QUEUE: {
        id: 'PER_QUEUE',
        defaultMessage: 'Per Queue',
      },
    });

    /**
     * Displayed as "Breakdown" select options to the user.
     * When a user selects the grouping option it will set the data adapter and display filter options
     * @param intl
     * @returns {DataGroupingOption[][]}
     */
    const categoryDefs = [
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.AGGREGATED }),
        category: 'AGGREGATED',
        apiParams: {
          type: 'domain',
        },
        adapter: 'callQueueSumToLineAdapter',
        filterObjects: ['subscriber', 'callqueue', 'department', 'site'],
      }),
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.PER_AGENT }),
        category: 'PER_AGENT',
        apiParams: {
          type: 'op_term_sub',
        },
        adapter: 'callQueueToLineAdapter',
        filterObjects: ['subscriber'],
      }),
      new DataGroupingOption({
        label: intl.formatMessage({ ...messages.PER_QUEUE }),
        category: 'PER_QUEUE',
        apiParams: {
          type: 'queue_name',
        },
        adapter: 'callQueueToLineAdapter',
        filterObjects: ['callqueue', 'department', 'site'],
      }),
    ];

    const categoryDefsNoAgent = categoryDefs.filter((option) => option.category === 'AGGREGATED'
      || option.category === 'PER_QUEUE');

    const hourOffset = getHoursOffset();

    /**
     * Default callqueuereport api params
     * @type {{object: string, action: string, format: string, range_interval: string, value_offset: string}}
     */
    const apiParams = {
      object: 'callqueuereport',
      action: 'read',
      format: 'json',
      range_interval: hourOffset,
      value_offset: hourOffset,
      unit: 'seconds',
    };

    const statMessages = new StatMessages(intl);

    /**
     * add socket subscriptions and events if any
     * Example Props
     * stat: 'AC'
     * socketSubscriptions: [{ type: 'agent', domain: string, user: string, filter: string }]
     * socketEvents: [{ name: string, adapter: function, adapterParams: object }] }
     * @type {*[]}
     */
    const stats = [
      {
        stat: 'AC',
        ...statMessages.messages.AC,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue', 'site', 'department'],
      },
      { stat: 'AH', ...statMessages.messages.AH },
      { stat: 'ATT', ...statMessages.messages.ATT },
      { stat: 'AWT', ...statMessages.messages.AWT },
      { stat: 'CH', ...statMessages.messages.CH },
      {
        stat: 'FWD',
        ...statMessages.messages.FWD,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue', 'site', 'department'],
      },
      {
        stat: 'VM',
        ...statMessages.messages.VM,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue', 'site', 'department'],
      },
      {
        stat: 'VOL',
        ...statMessages.messages.VOL,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue', 'site', 'department'],
      },
      { stat: 'TT', ...statMessages.messages.TT },
      { stat: 'AHT', ...statMessages.messages.AHT },
      {
        stat: 'Peak',
        ...statMessages.messages.Peak,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue'],
        filterMultiples: false,
      },
      {
        stat: 'CW',
        ...statMessages.messages.CW,
        categoryDefs: categoryDefsNoAgent,
        filterCategories: ['callqueue'],
        filterMultiples: false,
      },
    ];

    /**
     * @type {DataOption[]}
     */
    const options = stats.map((stat) => new DataOption({
      type: 'api',
      apiParams: { ...apiParams, stat: stat.stat },
      categoryDefs: stat.categoryDefs ? stat.categoryDefs : categoryDefs,
      hasCategories: true,
      hasColumns: false,
      supportedCharts: ['ResponsiveLine'],
      xAxis: 'datetime',
      ...stat,
    }));

    this.dataOptions = _.keyBy(options, 'stat');
  }
}
